/**
 * Enhanced Quotation Service
 * Direct Database persistence with performance optimizations, caching, and monitoring.
 * Uses SupabaseAdapter with enhanced performance tracking.
 */

import { SupabaseAdapter } from '@/lib/persistence/supabaseAdapter';
import { performanceMonitor } from '@/lib/utils/performanceMonitor';
import { cacheService } from '@/lib/utils/cacheService';
import type {
  Quotation,
  QuotationMetadata,
  LineItem,
  RoomInstance,
  RoomCategory,
  QuotationSection,
  RoomItemGroup,
  PaymentMilestone,
  RevisionLogEntry
} from '@/types/quotation';
import { DEFAULT_TERMS, DEFAULT_PAYMENT_MILESTONES, isImmutableStatus } from '@/types/quotation';
import type { QuotationRevision } from '@/types/revision';
import type { Database, Json } from '@/integrations/supabase/types';
import { supabase } from '@/integrations/supabase/client';
import { settingsService } from './settingsService';
import { normalizeTextValue } from '@/utils/textNormalization';
import { logger } from '@/lib/utils/logger';
import { tenantService } from './tenantService';
import { calculateNetTotal, type Dimensions } from '@/utils/pricingEngine';
import { calculateProjectFinancials } from '@/utils/financials';

// Database-aligned types
export type QuotationDB = Database['public']['Tables']['quotations']['Row'] & {
  user_id?: string | null;
  rooms?: Json;
  section_metadata?: Json;
  issued_on?: string | null;
  validity_days?: number | null;
  approval_note?: string | null;
};
export type LineItemDB = Database['public']['Tables']['quotation_line_items']['Row'];
export type RevisionDB = Database['public']['Tables']['quotation_revisions']['Row'];

// Section metadata type
interface SectionMetadata {
  id: string;
  name: string;
  order: number;
  workType?: string;
  gstEnabled?: boolean;
}

// Create direct Supabase adapters
const quotationAdapter = new SupabaseAdapter<QuotationDB>('quotations');
const lineItemAdapter = new SupabaseAdapter<LineItemDB>('quotation_line_items');
const revisionAdapter = new SupabaseAdapter<RevisionDB>('quotation_revisions');

// NEW: Adapters for Drafts and Permissions
const draftAdapter = new SupabaseAdapter<unknown>('quotation_drafts');
const permissionAdapter = new SupabaseAdapter<unknown>('quotation_permissions');

/**
 * Convert database format to application Quotation format
 */
const mapDatabaseToQuotation = (db: QuotationDB, lineItems: LineItemDB[] = []): Quotation => {
  const clientInfo = db.client_info as Record<string, unknown> || {};
  const companyInfo = db.company_info as Record<string, unknown> || {};
  const financials = db.financials as Record<string, unknown> || {};

  // Robust Fallback for Terms: Use remote settings defaults or hardcoded DEFAULT_TERMS
  let defaultTerms = DEFAULT_TERMS;
  let defaultPaymentSchedule = DEFAULT_PAYMENT_MILESTONES;

  const prefs = settingsService.getPreferences();
  if (prefs.default_terms) defaultTerms = prefs.default_terms;
  if (prefs.default_payment_schedule) defaultPaymentSchedule = prefs.default_payment_schedule;

  const rawTerms = db.terms_info as Record<string, unknown>;
  const termsInfo = (rawTerms && Object.keys(rawTerms).length > 0)
    ? rawTerms
    : defaultTerms;

  // Robust Fallback for Payment Schedule
  const rawPaymentSchedule = db.payment_schedule as unknown as PaymentMilestone[];
  const paymentSchedule = (rawPaymentSchedule && rawPaymentSchedule.length > 0)
    ? rawPaymentSchedule
    : defaultPaymentSchedule.map((m: unknown) => ({ ...m, id: crypto.randomUUID(), amount: 0 }));

  // PRESERVE STRUCTURE: 
  let rooms: RoomInstance[] = ((db.rooms as unknown as RoomInstance[]) || []).map((r: any) => ({
    ...r,
    id: r.id || r.name || 'room',
    displayName: r.displayName || r.name || r.customName || r.id || 'Room'
  }));

  if (rooms.length === 0) {
    // Legacy fallback: Derive rooms from line items
    const uniqueRooms = new Map<string, RoomInstance>();
    lineItems.forEach(item => {
      const roomName = item.room_name || 'General';
      if (!uniqueRooms.has(roomName)) {
        let category: RoomCategory = 'custom';
        const lowercaseName = roomName.toLowerCase();
        if (lowercaseName.includes('bedroom')) category = 'bedroom';
        else if (lowercaseName.includes('kitchen')) category = 'kitchen';
        else if (lowercaseName.includes('living')) category = 'living';
        else if (lowercaseName.includes('dining')) category = 'dining';
        else if (lowercaseName.includes('bathroom')) category = 'bathroom';
        else if (lowercaseName.includes('utility')) category = 'utility';
        else if (lowercaseName.includes('balcony')) category = 'balcony';

        uniqueRooms.set(roomName, {
          id: roomName,
          category,
          instanceNumber: 1,
          displayName: roomName
        });
      }
    });
    rooms = Array.from(uniqueRooms.values());
  }

  const sectionMeta = (db.section_metadata as unknown as SectionMetadata[]) || [];

  const mappedSections = mapLineItemsToSections(lineItems, sectionMeta, rooms);
    const parsedFinancials = financials as unknown as Quotation['financials'];
    const calculatedSubtotal = mappedSections.reduce((acc, s) => acc + (s.sectionSubtotal || 0), 0);

    let finalFinancials = parsedFinancials;
    if ((!parsedFinancials || !parsedFinancials.grandTotal || parsedFinancials.grandTotal === 0 || !parsedFinancials.subtotal || parsedFinancials.subtotal === 0) && calculatedSubtotal > 0) {
      const calcResult = calculateProjectFinancials(
        mappedSections,
        parsedFinancials || {
          discountType: 'percentage',
          discountValue: 0,
          discountAmount: 0,
          taxName: 'GST',
          taxRate: 18,
          taxAmount: 0,
          subtotal: 0,
          grandTotal: 0,
          paymentScheduleAuto: true
        },
        paymentSchedule || []
      );

      finalFinancials = {
        ...(parsedFinancials || {}),
        subtotal: calcResult.subtotal,
        taxAmount: calcResult.taxAmount,
        discountAmount: calcResult.discountAmount,
        grandTotal: calcResult.grandTotal,
        discountType: parsedFinancials?.discountType || 'percentage',
        discountValue: parsedFinancials?.discountValue || 0,
        taxName: parsedFinancials?.taxName || 'GST',
        taxRate: parsedFinancials?.taxRate || 18,
        paymentScheduleAuto: parsedFinancials?.paymentScheduleAuto ?? true
      } as Quotation['financials'];
    }

    return {
    metadata: {
      quoteId: db.id,
      quoteNumber: db.quote_number,
      fileNumber: db.file_number || 'TBD',
      estimateType: db.estimate_type as QuotationMetadata['estimateType'],
      version: db.version,
      status: db.status as QuotationMetadata['status'],
      locked: db.is_locked,
      issuedOn: db.issued_on || db.created_at,
      validityDays: db.validity_days || 30,
      bdmId: db.bdm_id || undefined,
      notes: (db as any).approval_note || undefined,
      industryId: (db as any).industry_id || undefined,
      branding_snapshot: (db as any).branding_snapshot || undefined,
      isTemplate: (db as any).is_template ?? (clientInfo as Record<string, any>)?.isTemplate ?? false,
      templateName: (db as any).template_name ?? (clientInfo as Record<string, any>)?.templateName ?? undefined,
      // ── Lineage fields (from DB, never written by client) ──
      groupId: (db as any).group_id || undefined,
      parentId: (db as any).parent_id || undefined,
      revisionType: (db as any).revision_type || undefined,
      isLatestVersion: (db as any).is_latest_version ?? true,
      supersededBy: (db as any).superseded_by || undefined,
      finalizedAt: (db as any).finalized_at || undefined,
      finalizedBy: (db as any).finalized_by || undefined,
      supersededAt: (db as any).superseded_at || undefined,
      cancelledAt: (db as any).cancelled_at || undefined,
      cancelledBy: (db as any).cancelled_by || undefined,
      cancellationReason: (db as any).cancellation_reason || undefined,
    },
    client: clientInfo as unknown as Quotation['client'],
    company: companyInfo as unknown as Quotation['company'],
    financials: finalFinancials,
    terms: termsInfo as unknown as Quotation['terms'],
    proposalNarrative: (rawTerms as Record<string, any>)?.proposalNarrative || undefined,
    rooms,
    sections: mappedSections,
    paymentSchedule: paymentSchedule,
    revisionHistory: [],
    system: {
      createdAt: db.created_at,
      updatedAt: db.updated_at,
      createdBy: db.user_id || (db as any).created_by || 'system',
      updatedBy: 'system',
      templateVersion: '2.0.0'
    }
  };
};

/**
 * Group line items into sections with template preservation
 */
/**
 * Group line items into sections with template preservation
 * Robust handling of room matching and section grouping
 */
const mapLineItemsToSections = (
  lineItems: LineItemDB[],
  sectionMeta: SectionMetadata[] = [],
  allRooms: RoomInstance[] = []
): QuotationSection[] => {
  const sections: Record<string, QuotationSection> = {};

  // 1. Initialize from metadata if available
  if (sectionMeta && Array.isArray(sectionMeta)) {
    sectionMeta.forEach(meta => {
      const sectionName = meta.name ? String(meta.name).trim() : 'Modular Works';
      sections[sectionName] = {
        id: meta.id || sectionName,
        name: sectionName,
        workType: (meta.workType || 'modular') as QuotationSection['workType'],
        gstEnabled: meta.gstEnabled ?? false,
        sectionSubtotal: 0,
        roomGroups: allRooms.map(room => ({
          roomId: room.id,
          roomName: room.displayName || (room as any).name || room.id || 'Room',
          items: [],
          subtotal: 0
        }))
      };
    });
  }

  // 2. Map items to sections and rooms
  for (const item of lineItems) {
    const rawSectionName = (item.work_section || 'Modular Works').trim();
    const rawRoomName = (item.room_name || 'General').trim();

    // Section matching (Case insensitive map)
    let sectionName = Object.keys(sections).find(k => k.toLowerCase() === rawSectionName.toLowerCase());

    if (!sectionName) {
      sectionName = rawSectionName;
      sections[sectionName] = {
        id: sectionName,
        name: sectionName,
        workType: (item.category as unknown) || 'modular',
        gstEnabled: false,
        sectionSubtotal: 0,
        roomGroups: allRooms.map(room => ({
          roomId: room.id,
          roomName: room.displayName || (room as any).name || room.id || 'Room',
          items: [],
          subtotal: 0
        }))
      };
    }

    const section = sections[sectionName];

    // Room matching (Case insensitive & ID check)
    let group = section.roomGroups.find(g =>
      (g.roomName || '').toLowerCase() === rawRoomName.toLowerCase() ||
      g.roomId === rawRoomName
    );

    if (!group) {
      group = { roomId: rawRoomName, roomName: rawRoomName, items: [], subtotal: 0 };
      section.roomGroups.push(group);
    }

    const rawTotal = Number(item.total || 0);
    const subItemsTotal = (item.sub_items as any[])?.reduce((sum: number, si: any) => sum + Number(si.subTotal || 0), 0) || 0;
    const computedNetTotal = calculateNetTotal(
      Number(item.quantity || 0),
      Number(item.rate || 0),
      item.uom,
      (item.dimensions as unknown) as Dimensions
    ).total;

    const calculatedTotal = rawTotal > 0 
      ? rawTotal 
      : (subItemsTotal > 0 ? subItemsTotal : computedNetTotal);

    const mappedItem: LineItem = {
      id: item.id,
      slNo: group.items.length + 1,
      description: item.description || '',
      specifications: item.specifications || undefined,
      quantity: Number(item.quantity || 0),
      uom: item.uom as LineItem['uom'],
      rate: Number(item.rate || 0),
      total: calculatedTotal,
      dimensions: (item.dimensions as unknown) as LineItem['dimensions'],
      category: (item.category as unknown) || undefined,
      catalog_item_id: item.catalog_item_id || undefined,
      subItems: (item.sub_items as unknown as LineItem['subItems']) || undefined
    };

    group.items.push(mappedItem);
    group.subtotal += mappedItem.total;
    section.sectionSubtotal += mappedItem.total;
  }

  // Final cleanup: Remove empty sections that weren't in metadata
  // or return them all if meta was empty
  return Object.values(sections);
};

/**
 * Convert Quotation to database format
 */
const mapQuotationToDatabase = (quotation: Quotation): Partial<Database['public']['Tables']['quotations']['Insert']> & Record<string, unknown> => {
  const clientInfo = quotation.client || {};
  const clientName = clientInfo.name || 'Unknown Client';
  const projectName = clientInfo.projectName || 'Unknown Project';
  const generatedTitle = `${clientName} - ${projectName}`;
  const creatorId = quotation.system?.createdBy && quotation.system.createdBy !== 'system' ? quotation.system.createdBy : null;

  return {
    title: quotation.title || generatedTitle,
    client_name: clientInfo.name || null,
    project_name: clientInfo.projectName || null,
    quote_number: quotation.metadata.quoteNumber,
    file_number: quotation.metadata.fileNumber || null,
    estimate_type: quotation.metadata.estimateType,
    version: quotation.metadata.version,
    status: quotation.metadata.status,
    is_locked: quotation.metadata.locked,
    user_id: creatorId,
    created_by: creatorId,
    client_info: {
      ...(quotation.client || {}),
      isTemplate: quotation.metadata.isTemplate ?? false,
      templateName: quotation.metadata.templateName || null,
    } as unknown as Json,
    company_info: quotation.company as unknown as Json,
    financials: quotation.financials as unknown as Json,
    terms_info: { ...(quotation.terms as any), proposalNarrative: quotation.proposalNarrative } as unknown as Json,
    bdm_id: quotation.metadata.bdmId || null,
    payment_schedule: quotation.paymentSchedule as unknown as Json,
    rooms: quotation.rooms as unknown as Json,
    section_metadata: quotation.sections.map(s => ({
      id: s.id,
      name: s.name,
      workType: s.workType,
      gstEnabled: s.gstEnabled
    })) as unknown as Json,
    approval_note: quotation.metadata.notes || null,
    industry_id: quotation.metadata.industryId || null,
    branding_snapshot: quotation.metadata.branding_snapshot || null,
    is_template: quotation.metadata.isTemplate ?? false,
    template_name: quotation.metadata.templateName || null
  } as Partial<Database['public']['Tables']['quotations']['Insert']> & Record<string, unknown>;
};

/**
 * Convert QuotationRevision to database format
 */
const mapRevisionToDatabase = (
  revision: QuotationRevision,
  quotation: Quotation,
  userId?: string | null,
  tenantId?: string | null
): Record<string, unknown> => {
  return {
    id: revision.id,
    quote_id: revision.quote_id,
    version: revision.revision_number,
    snapshot: quotation as unknown as Json,
    line_items_snapshot: extractLineItems(quotation, revision.quote_id, tenantId) as unknown as Json,
    change_summary: revision.what_changed || null,
    created_by: revision.changed_by || userId || 'system',
    created_at: revision.changed_at || new Date().toISOString()
  };
};


/**
 * Extract line items from quotation sections
 */
const extractLineItems = (quotation: Quotation, quoteId: string, tenantId?: string | null): LineItemDB[] => {
  const items: LineItemDB[] = [];
  let sortOrder = 0;
  const now = new Date().toISOString();

  if (!quotation.sections || !Array.isArray(quotation.sections)) {
    return [];
  }

  for (const section of quotation.sections) {
    if (!section.roomGroups || !Array.isArray(section.roomGroups)) continue;

    for (const group of section.roomGroups) {
      if (!group.items || !Array.isArray(group.items)) continue;

      for (const item of group.items) {
        const qty = Number(item.quantity || 0);

        const itemId = item.id && item.id.length > 5 ? item.id : crypto.randomUUID();

        items.push({
          id: itemId,
          quote_id: quoteId,
          tenant_id: tenantId || null,
          room_name: group.roomName || group.roomId,
          description: item.description || 'Quick Item',
          specifications: item.specifications || null,
          category: item.category || section.workType || 'modular',
          quantity: qty,
          uom: item.uom || 'Sqft',
          rate: Number(item.rate || 0),
          total: Number(item.total || 0),
          dimensions: (item.dimensions || {}) as Json,
          catalog_item_id: item.catalog_item_id || null,
          sort_order: sortOrder++,
          work_section: section.name || null,
          sub_items: (item.subItems as unknown as Json) || null,
          created_at: now,
          updated_at: now,
        } as LineItemDB);
      }
    }
  }

  return items;
};

class QuotationService {
  // In-flight promise deduplication — prevents parallel identical DB fetches
  private _catalogFetchInFlight: Promise<unknown[]> | null = null;
  private _quotationsFetchInFlight: Promise<Quotation[]> | null = null;
  // Session-scoped tenant ID cache — persisted to sessionStorage to survive hot reloads
  private get _cachedTenantId(): string | null {
    return sessionStorage.getItem('_dew_tid');
  }
  private set _cachedTenantId(v: string | null) {
    if (v) sessionStorage.setItem('_dew_tid', v);
    else sessionStorage.removeItem('_dew_tid');
  }

  /**
   * Save quotation (Direct to Supabase)
   */
  /**
   * Save quotation (Direct to Supabase)
   */
  async saveQuotation(quotation: Quotation, revision?: QuotationRevision, isForcedSave = true): Promise<string> {
    const normalizedQuotation = normalizeTextValue(quotation);
    const { metadata } = normalizedQuotation;
    const quoteId = metadata.quoteId || crypto.randomUUID();
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    const tenant = await tenantService.getCurrentTenant();
    const tenantId = tenant?.id;

    const effectiveUserId = userId || (normalizedQuotation.system?.createdBy && normalizedQuotation.system.createdBy !== 'system' ? normalizedQuotation.system.createdBy : null);

    const quoteData = {
      ...mapQuotationToDatabase(normalizedQuotation),
      id: quoteId,
      user_id: effectiveUserId,
      created_by: effectiveUserId,
      tenant_id: tenantId || null
    } as unknown;

    // 1. Temporarily unlock existing finalized quote in DB so line items can be updated without triggering trg_line_item_immutability
    const { data: existingQuote } = await supabase
      .from('quotations')
      .select('status, is_locked')
      .eq('id', quoteId)
      .maybeSingle();

    if (existingQuote && (existingQuote.status === 'finalized' || existingQuote.is_locked)) {
      await supabase
        .from('quotations')
        .update({ status: 'draft', is_locked: false })
        .eq('id', quoteId);
    }

    // 2. Extract and save line items
    const lineItems = extractLineItems(normalizedQuotation, quoteId, tenantId);

    // Fetch existing items for Safety Check / Deletion
    const { data: existingDBItems } = await supabase
      .from('quotation_line_items')
      .select('id')
      .eq('quote_id', quoteId);

    // SAFETY GUARD: Prevent accidental data loss
    if (!isForcedSave && lineItems.length === 0 && existingDBItems && existingDBItems.length > 0) {
      console.warn('[QuotationService] Safety Guard: blocking accidental wipe during auto-save.');
      return quoteId;
    }

    if (existingDBItems) {
      const currentIds = new Set(lineItems.map(i => i.id));
      const idsToDelete = existingDBItems
        .map(i => i.id)
        .filter(id => !currentIds.has(id));

      if (idsToDelete.length > 0) {
        const deleteResult = await lineItemAdapter.bulkDelete(idsToDelete);
        if (!deleteResult.success) {
          console.error(`[QuotationService] Delete Failed: ${deleteResult.error}`);
        }
      }
    }

    if (lineItems.length > 0) {
      const BATCH_SIZE = 50;
      for (let i = 0; i < lineItems.length; i += BATCH_SIZE) {
        const batch = lineItems.slice(i, i + BATCH_SIZE);
        const batchResult = await lineItemAdapter.bulkUpsert(batch);
        if (!batchResult.success) {
          throw new Error(`Line Items Batch Save Failed: ${batchResult.error}`);
        }
      }
    }

    // 3. Save Payment Schedule
    if (metadata.status === 'finalized' && tenantId) {
      try {
        const totalPercentage = normalizedQuotation.paymentSchedule.reduce((sum, m) => sum + (m.percentage || 0), 0);
        if (Math.abs(totalPercentage - 100) <= 0.01 && normalizedQuotation.paymentSchedule.length > 0) {
          const { data: existingSchedules } = await supabase
            .from('payment_schedules')
            .select('id, client_uid, status, demand_eligibility_status, percentage, amount, archived_at')
            .eq('quote_id', quoteId);

          const immutableStatuses = ['paid', 'invoiced', 'locked', 'partially_paid'];
          const existingMap = new Map(existingSchedules?.map(s => [s.client_uid, s]) || []);
          const schedulesToUpsert: any[] = [];
          const payloadClientUids = new Set();
          const payloadIds = new Set();

          normalizedQuotation.paymentSchedule.forEach((milestone, index) => {
            const clientUid = milestone.id || crypto.randomUUID();
            payloadClientUids.add(clientUid);
            if (milestone.id) payloadIds.add(milestone.id);
            const existing = existingMap.get(clientUid);
            if (!existing || !immutableStatuses.includes(existing.status)) {
              schedulesToUpsert.push({
                ...(existing ? { id: existing.id } : {}),
                client_uid: clientUid,
                tenant_id: tenantId,
                quote_id: quoteId,
                milestone_name: milestone.milestoneName,
                percentage: milestone.percentage,
                amount: milestone.amount,
                status: existing ? existing.status : 'pending',
                demand_eligibility_status: existing ? existing.demand_eligibility_status : 'not_eligible',
                sort_order: index,
                due_condition: (milestone as any).description || null,
                source: 'custom',
                archived_at: null,
                updated_at: new Date().toISOString()
              });
            }
          });

          if (schedulesToUpsert.length > 0) {
            await supabase
              .from('payment_schedules')
              .upsert(schedulesToUpsert, { onConflict: 'quote_id, client_uid' });
          }
        }
      } catch (err) {
        console.error('[QuotationService] Payment Schedule sync error:', err);
      }
    }

    // 4. Save Revision Entry (or auto-generate if missing for finalized/versioned quotes)
    let finalRevision = revision;
    if (!finalRevision && (metadata.status === 'finalized' || (metadata.version && metadata.version > 0))) {
      finalRevision = {
        id: crypto.randomUUID(),
        quote_id: quoteId,
        revision_number: metadata.version || 1,
        quotation_snapshot: normalizedQuotation,
        line_items_snapshot: extractLineItems(normalizedQuotation, quoteId, tenantId),
        changed_at: new Date().toISOString(),
        changed_by: effectiveUserId || 'system',
        what_changed: metadata.status === 'finalized' ? 'Finalized quotation snapshot' : 'Saved quotation snapshot'
      };
    }

    if (finalRevision) {
      const revData = mapRevisionToDatabase(finalRevision, normalizedQuotation, effectiveUserId, tenantId);
      const revResult = await revisionAdapter.upsert(revData as RevisionDB);
      if (!revResult.success) {
        console.error(`[QuotationService] Revision Save Failed: ${revResult.error}`);
      }
    }

    // 5. Finally, save/lock Quotation Header
    const quoteResult = await quotationAdapter.upsert(quoteData as QuotationDB);
    if (!quoteResult.success) {
      const msg = `Quotation Header Save Failed: ${quoteResult.error}`;
      console.error(msg);
      throw new Error(msg);
    }

    // Invalidate stale caches so subsequent reads immediately return fresh DB data
    cacheService.delete(`quotation_${quoteId}`);
    cacheService.delete('quotations_all');

    return quoteId;
  }


  async getQuotations(useCache: boolean = true): Promise<QuotationDB[]> {
    const cacheKey = 'quotations_all';

    if (useCache) {
      const cached = cacheService.get<QuotationDB[]>(cacheKey);
      if (cached) {
        logger.log('[QuotationService] Returning cached quotations');
        return cached;
      }
    }

    const startTime = performance.now();
    const result = await quotationAdapter.getAll();
    const duration = performance.now() - startTime;

    performanceMonitor.trackDBQuery('GET_ALL_QUOTATIONS', duration, 'quotations');

    if (result.success && result.data && useCache) {
      // Cache for 10 minutes
      cacheService.set(cacheKey, result.data, 10 * 60 * 1000);
    }

    return result.data || [];
  }

  async getRecentQuotations(limit: number = 50, useCache: boolean = true): Promise<Quotation[]> {
    const cacheKey = `recent_quotations_${limit}`;

    if (useCache) {
      const cached = cacheService.get<Quotation[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Deduplicate parallel in-flight fetches
    if (this._quotationsFetchInFlight) {
      return this._quotationsFetchInFlight;
    }

    const startTime = performance.now();
    this._quotationsFetchInFlight = (async () => {
      try {
        const tenant = await tenantService.getCurrentTenant();
        const tenantId = tenant?.id;

        // Attempt 1: Query registry view
        let query = supabase
          .from('quotation_registry')
          .select(`
            id,
            quote_number,
            file_number,
            estimate_type,
            version,
            status,
            is_locked,
            client_info,
            financials,
            bdm_id,
            issued_on,
            validity_days,
            industry_id,
            group_id,
            parent_id,
            revision_type,
            is_latest_version,
            superseded_by,
            is_template,
            template_name,
            created_at,
            updated_at,
            user_id
          `);

        if (tenantId) {
          query = query.eq('tenant_id', tenantId);
        }

        let { data, error } = await query
          .order('created_at', { ascending: false })
          .limit(limit);

        // Fallback: If registry view returns empty or errors, query main quotations table directly
        if (error || !data || data.length === 0) {
          let directQuery = supabase
            .from('quotations')
            .select(`
              id,
              quote_number,
              file_number,
              estimate_type,
              version,
              status,
              is_locked,
              client_info,
              financials,
              bdm_id,
              issued_on,
              validity_days,
              industry_id,
              group_id,
              parent_id,
              revision_type,
              is_latest_version,
              superseded_by,
              is_template,
              template_name,
              created_at,
              updated_at,
              user_id
            `);

          if (tenantId) {
            directQuery = directQuery.eq('tenant_id', tenantId);
          }

          const fallbackResult = await directQuery
            .or('is_latest_version.eq.true,is_latest_version.is.null')
            .order('created_at', { ascending: false })
            .limit(limit);

          if (!fallbackResult.error && fallbackResult.data && fallbackResult.data.length > 0) {
            data = fallbackResult.data;
          }
        }

        const duration = performance.now() - startTime;
        performanceMonitor.trackDBQuery(`GET_RECENT_QUOTATIONS_${limit}`, duration, 'quotations');

        const quotations = (data || []).map(db => mapDatabaseToQuotation({
          ...db,
          rooms: [],
          section_metadata: null,
          user_id: (db as any).user_id || null
        } as QuotationDB));

        if (useCache && quotations.length > 0) {
          cacheService.set(cacheKey, quotations, 5 * 60 * 1000);
        }

        return quotations;
      } catch (error) {
        const duration = performance.now() - startTime;
        performanceMonitor.trackDBQuery(`GET_RECENT_QUOTATIONS_${limit}_FAILED`, duration, 'quotations');
        console.error('[QuotationService] Failed to fetch recent quotations:', error);
        return [];
      } finally {
        this._quotationsFetchInFlight = null;
      }
    })();

    return this._quotationsFetchInFlight;
  }

  async getQuotationById(id: string, useCache: boolean = true): Promise<Quotation | null> {
    const cacheKey = `quotation_${id}`;

    if (useCache) {
      const cached = cacheService.get<Quotation>(cacheKey);
      if (cached) {
        logger.log(`[QuotationService] Returning cached quotation ${id}`);
        return cached;
      }
    }

    const startTime = performance.now();
    try {
      const quoteResult = await quotationAdapter.getById(id);
      if (!quoteResult.success || !quoteResult.data) return null;

      // Fetch line items and revisions in parallel for better performance
      const [lineItemsRes, revisionsRes] = await Promise.all([
        supabase
          .from('quotation_line_items')
          .select('*')
          .eq('quote_id', id)
          .order('sort_order', { ascending: true }),
        supabase
          .from('quotation_revisions')
          .select('id, version, change_summary, created_by, created_at')
          .eq('quote_id', id)
          .order('version', { ascending: false })
          .limit(10)
      ]);

      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`GET_QUOTATION_${id}`, duration, 'quotations');

      const lineItems = (lineItemsRes.data || []) as LineItemDB[];
      const revisionLogs: RevisionLogEntry[] = (revisionsRes.data || []).map(rev => ({
        revisionNumber: rev.version,
        date: rev.created_at || new Date().toISOString(),
        changedBy: rev.created_by || 'Unknown',
        whatChanged: rev.change_summary || 'Updated quotation'
      }));

      const quotation = mapDatabaseToQuotation(quoteResult.data, lineItems);
      const result = {
        ...quotation,
        revisionHistory: revisionLogs
      };

      if (useCache) {
        // Cache for 3 minutes (shorter for frequently changing data)
        cacheService.set(cacheKey, result, 3 * 60 * 1000);
      }

      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      performanceMonitor.trackDBQuery(`GET_QUOTATION_${id}_FAILED`, duration, 'quotations');
      console.error(`[QuotationService] Failed to fetch quotation ${id}:`, error);
      return null;
    }
  }

  async deleteQuotation(id: string): Promise<boolean> {
    const result = await quotationAdapter.delete(id);
    return result.success;
  }

  async updateQuotation(id: string, updates: Partial<QuotationDB>): Promise<boolean> {
    const result = await quotationAdapter.update(id, updates);
    if (result.success) {
      cacheService.delete(`quotation_${id}`);
      cacheService.delete('quotations_all');
    }
    return result.success;
  }

  async getPermissions(quoteId: string): Promise<Record<string, unknown>[]> {
    const { data, error } = await supabase
      .from('quotation_permissions')
      .select('*, profile:profiles(email, full_name)')
      .eq('quote_id', quoteId);

    if (error) {
      console.error('[QuotationService] Error fetching permissions:', error);
      return [];
    }
    return data || [];
  }

  async setPermission(quoteId: string, userId: string, role: 'viewer' | 'editor' | 'user' | 'admin'): Promise<boolean> {
    const result = await permissionAdapter.upsert({
      quote_id: quoteId,
      user_id: userId,
      role
    } as unknown);
    return result.success;
  }

  async deletePermission(quoteId: string, userId: string): Promise<boolean> {
    const { error } = await supabase
      .from('quotation_permissions')
      .delete()
      .match({ quote_id: quoteId, user_id: userId });
    return !error;
  }

  async getDraft(quoteId: string): Promise<Record<string, unknown> | null> {
    const result = await draftAdapter.getById(quoteId, 'quote_id');
    return result.success ? result.data : null;
  }

  async saveDraft(quotation: Quotation): Promise<boolean> {
    const normalizedQuotation = normalizeTextValue(quotation);
    if (isImmutableStatus(normalizedQuotation.metadata.status)) {
      return true;
    }
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;

    logger.log(`[QuotationService] Draft Attempt - Quote: ${normalizedQuotation.metadata.quoteId} | User: ${userId || 'ANONYMOUS'}`);

    const result = await draftAdapter.upsert({
      quote_id: normalizedQuotation.metadata.quoteId,
      content: normalizedQuotation,
      updated_by: userId,
      updated_at: new Date().toISOString()
    } as unknown, 'quote_id');
    return result.success;
  }

  async deleteDraft(quoteId: string): Promise<boolean> {
    const { error } = await supabase
      .from('quotation_drafts')
      .delete()
      .eq('quote_id', quoteId);
    return !error;
  }

  // Legacy/Compatibility methods (Simplified)
  async getCatalogItems(forceFetch = false, useCache = true): Promise<Record<string, unknown>[]> {
    const cacheKey = 'catalog_items_active';

    if (useCache && !forceFetch) {
      const cached = cacheService.get<unknown[]>(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Deduplicate parallel in-flight fetches
    if (this._catalogFetchInFlight) {
      return this._catalogFetchInFlight;
    }

    const startTime = performance.now();
    this._catalogFetchInFlight = (async () => {
      try {
        // Only select columns actually used by the app
        const { data, error } = await supabase
          .from('catalog_items')
          .select('id, name, category, specifications, default_rate, default_uom, has_dimensions, is_active, room_type')
          .eq('is_active', true)
          .order('category', { ascending: true })
          .order('name', { ascending: true });

        const duration = performance.now() - startTime;
        performanceMonitor.trackDBQuery('GET_CATALOG_ITEMS', duration, 'catalog_items');

        if (error) {
          console.error('[QuotationService] Error fetching catalog items:', error);
          return [];
        }

        const items = data || [];
        if (useCache && items.length > 0) {
          // Catalog changes rarely — cache for 30 minutes
          cacheService.set(cacheKey, items, 30 * 60 * 1000);
        }
        return items;
      } catch (error) {
        const duration = performance.now() - startTime;
        performanceMonitor.trackDBQuery('GET_CATALOG_ITEMS_FAILED', duration, 'catalog_items');
        console.error('[QuotationService] Failed to fetch catalog items:', error);
        return [];
      } finally {
        this._catalogFetchInFlight = null;
      }
    })();

    return this._catalogFetchInFlight;
  }

  async getMasterCatalog() { return this.getCatalogItems(); }
  async getBrands() { return []; }
  async getBrandPricing() { return []; }
  async getGlobalSettings() {
    const { settingsService } = await import('./settingsService');
    return settingsService.getPreferences();
  }

  /**
   * Differential section save — writes only changed sections to the DB.
   */
  async saveQuotationSections(quotationId: string, sections: import('@/types/quotation').QuotationSection[]): Promise<string> {
    const { data, error } = await supabase
      .from('quotations')
      .update({
        section_metadata: sections as unknown as import('@/integrations/supabase/types').Json,
        updated_at: new Date().toISOString(),
      })
      .eq('id', quotationId)
      .select('id')
      .single();

    if (error) {
      console.error('[QuotationService] saveQuotationSections failed:', error);
      throw new Error(`Section save failed: ${error.message}`);
    }

    return data.id as string;
  }

  // ────────────────────────────────────────────────────────────
  // Immutable Quotation Lifecycle Methods
  // All operations go through DB-side RPCs — no client-managed transactions
  // ────────────────────────────────────────────────────────────

  /**
   * Finalizes a draft quotation via the DB-side atomic RPC.
   * Atomically supersedes the previous finalized version in the same group.
   * Requires admin or owner tenant role.
   */
  async finalizeQuotation(quoteId: string): Promise<{ finalizedId: string; supersededId: string | null }> {
    const tenant = await tenantService.getCurrentTenant();
    const tenantId = tenant?.id;
    if (!tenantId) throw new Error('No tenant context');

    const { data, error } = await supabase.rpc('fn_finalize_quotation_revision', {
      p_new_version_id: quoteId,
      p_tenant_id: tenantId,
    });

    if (error) throw new Error(`Finalization failed: ${error.message}`);

    // Invalidate caches for this quote and the list
    cacheService.delete(`quotation_${quoteId}`);
    cacheService.delete('quotations_all');

    return {
      finalizedId: (data as any).finalized_id,
      supersededId: (data as any).superseded_id || null,
    };
  }

  /**
   * Creates a new draft revision from a finalized/cancelled/superseded quotation.
   * Returns the new draft quotation UUID.
  async createRevision(parentId: string): Promise<string> {
    const tenant = await tenantService.getCurrentTenant();
    let tenantId = tenant?.id;
    if (!tenantId) {
      const { data: quoteRow } = await supabase.from('quotations').select('tenant_id').eq('id', parentId).maybeSingle();
      if (quoteRow?.tenant_id) {
        tenantId = quoteRow.tenant_id;
      }
    }

    const { data, error } = await supabase.rpc('fn_create_quotation_revision', {
      p_parent_id: parentId,
      p_tenant_id: tenantId || null,
    });

    if (error) throw new Error(`Create revision failed: ${error.message}`);

    // Invalidate list cache — new row exists
    cacheService.delete('quotations_all');

    return data as string;
  }

  /**
   * Duplicates a quotation as a brand-new independent draft (new group_id).
   * Preserves BOQ structure and rates. Resets client identity.
   * Returns the new draft quotation UUID.
   */
  async duplicateAsDraft(sourceId: string): Promise<string> {
    const tenant = await tenantService.getCurrentTenant();
    const tenantId = tenant?.id;
    if (!tenantId) throw new Error('No tenant context');

    const { data, error } = await supabase.rpc('fn_duplicate_quotation_as_draft', {
      p_source_id: sourceId,
      p_tenant_id: tenantId,
    });

    if (error) throw new Error(`Duplicate failed: ${error.message}`);

    cacheService.delete('quotations_all');

    return data as string;
  }

  /**
   * Cancels a finalized quotation.
   * Commercial fields remain frozen — only status/reason are updated.
   * The DB-side immutability trigger validates that commercial fields are untouched.
   */
  async cancelQuotation(quoteId: string, reason: string): Promise<void> {
    if (!reason || reason.trim().length < 5) {
      throw new Error('Cancellation reason must be at least 5 characters.');
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('quotations')
      .update({
        status: 'cancelled',
        cancellation_reason: reason.trim(),
        cancelled_at: new Date().toISOString(),
        cancelled_by: user?.id || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', quoteId);

    if (error) {
      // The immutability trigger will raise a RAISE EXCEPTION if commercial fields
      // were somehow in the update payload — that becomes a Postgres error here.
      throw new Error(`Cancellation failed: ${error.message}`);
    }

    cacheService.delete(`quotation_${quoteId}`);
    cacheService.delete('quotations_all');
  }

  /**
   * Updates template status (is_template and template_name) for a quotation.
   * Performs a targeted column update to avoid triggering the DB immutability check on finalized quotes.
   */
  async toggleTemplateStatus(quoteId: string, isTemplate: boolean, templateName?: string): Promise<boolean> {
    try {
      // 1. Direct column update (does NOT mutate commercial fields checked by fn_enforce_quotation_immutability)
      const { error: colError } = await supabase
        .from('quotations')
        .update({
          is_template: isTemplate,
          template_name: isTemplate ? templateName : null,
          updated_at: new Date().toISOString(),
        } as any)
        .eq('id', quoteId);

      if (colError) {
        console.warn('[QuotationService] Direct column update for is_template failed:', colError.message);
      }

      // 2. Also update client_info JSONB if quotation is still in draft status
      const { data: current } = await supabase
        .from('quotations')
        .select('client_info, status')
        .eq('id', quoteId)
        .maybeSingle();

      if (current && current.status === 'draft') {
        const clientInfo = (current.client_info as Record<string, any>) || {};
        clientInfo.isTemplate = isTemplate;
        if (isTemplate && templateName) {
          clientInfo.templateName = templateName;
        } else {
          delete clientInfo.templateName;
        }
        await supabase
          .from('quotations')
          .update({ client_info: clientInfo } as any)
          .eq('id', quoteId);
      }

      cacheService.invalidateQuotations(quoteId);
      return true;
    } catch (err) {
      console.error('[QuotationService] Failed to toggle template status:', err);
      return false;
    }
  }
}

export const quotationService = new QuotationService();
